// Forgot/Reset password — ports auth.js setupForgotPasswordPage + handleForgotPassword
// + handleResetPassword. A ?token=... query switches from "request reset email"
// to "set a new password"; a successful reset logs the user in (TokenResponse).

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { authApi } from '../../api/auth'
import { messageFromError } from '../../api/client'
import PasswordInput from '../../components/PasswordInput'
import s from '../auth.module.css'

const MIN_PASSWORD = 8

export default function ForgotPassword() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const { resetPassword } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const requestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email) return setError('Введите email')
    setLoading(true)
    try {
      await authApi.forgotPassword({ email })
      setSent(true)
    } catch (err) {
      setError(messageFromError(err, 'Не удалось отправить ссылку'))
    } finally {
      setLoading(false)
    }
  }

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!token) return
    if (password.length < MIN_PASSWORD) return setError(`Пароль должен быть не менее ${MIN_PASSWORD} символов`)
    if (password !== passwordConfirm) return setError('Пароли не совпадают')
    setLoading(true)
    try {
      await resetPassword({ token, password })
      // useAuth navigated to chat on success
    } catch (err) {
      setError(messageFromError(err, 'Не удалось сбросить пароль'))
    } finally {
      setLoading(false)
    }
  }

  // Reset-with-token view
  if (token) {
    return (
      <div className={s.screen}>
        <div className={s.content}>
          <div className={s.header}>
            <h1 className={s.title}>Новый пароль</h1>
            <p className={s.subtitle}>Введите ваш новый пароль</p>
          </div>
          <form className={s.form} onSubmit={submitNewPassword}>
            <div className={s.fieldGroup}>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Новый пароль (минимум 8 символов)"
                autoComplete="new-password"
              />
              <PasswordInput
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Повторите пароль"
                autoComplete="new-password"
              />
            </div>
            {error && <p className={s.error}>{error}</p>}
            <button className={s.button} type="submit" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сбросить пароль'}
            </button>
          </form>
          <div className={s.authBottom}>
            <Link to="/login">Вернуться к входу</Link>
          </div>
        </div>
      </div>
    )
  }

  // Request-reset-email view
  return (
    <div className={s.screen}>
      <div className={s.content}>
        <div className={s.header}>
          <h1 className={s.title}>Восстановление</h1>
          <p className={s.subtitle}>Введите email, чтобы получить ссылку для сброса пароля</p>
        </div>

        {sent ? (
          <p className={s.success}>
            Если аккаунт с этим email существует, ссылка для сброса пароля отправлена.
          </p>
        ) : (
          <form className={s.form} onSubmit={requestReset}>
            <div className={s.fieldGroup}>
              <input
                className={s.input}
                type="email"
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {error && <p className={s.error}>{error}</p>}
            <button className={s.button} type="submit" disabled={loading}>
              {loading ? 'Отправка...' : 'Отправить ссылку'}
            </button>
          </form>
        )}

        <div className={s.authBottom}>
          <Link to="/login">Вернуться к входу</Link>
        </div>
      </div>
    </div>
  )
}
